import React, { useState } from 'react';
import { App as AntApp, Modal, Form, Input, Button } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';

import { dbUserAndHub } from '../config/api';
import { encryptUserAndHubPayload } from '../utils/encryption';
import { useAuth } from '../context/AuthContext';
import { HUB_SETUP } from '../config/zinihubConfig';
import { GUIDED_JOURNEY_TARGET } from '../config/guidedJourneyConfig';

import styles from './HubSetupBanner.module.css';

const HubSetupBanner = ({ visible, onComplete, onCancel }) => {
  const { message } = AntApp.useApp();
  const { token } = useAuth();
  const [form] = Form.useForm();

  const [checkingDomain, setCheckingDomain] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [domainAvailable, setDomainAvailable] = useState(null);
  const [checkedDomain, setCheckedDomain] = useState('');

  const handleCheckDomain = async () => {
    const domainName = form.getFieldValue('domainName');

    if (!domainName) {
      message.warning(HUB_SETUP.DOMAIN_ENTER_FIRST);
      return;
    }

    const domainRegex = /^[a-z0-9-]+$/;
    if (!domainRegex.test(domainName)) {
      message.error(HUB_SETUP.DOMAIN_PATTERN);
      return;
    }

    setCheckingDomain(true);
    setDomainAvailable(null);
    setCheckedDomain(domainName);

    try {
      const response = await axios.post(
        dbUserAndHub,
        encryptUserAndHubPayload({
          operationType: 'read',
          action: 'checkHubDomainAvailability',
          domainName,
          token,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data?.available === true) {
        setDomainAvailable(true);
        message.success(HUB_SETUP.DOMAIN_AVAILABLE);
      } else {
        setDomainAvailable(false);
        message.error(HUB_SETUP.DOMAIN_TAKEN);
      }
    } catch (error) {
      setDomainAvailable(false);
      console.error('Domain check error:', error);
      message.error(HUB_SETUP.DOMAIN_CHECK_ERROR);
    } finally {
      setCheckingDomain(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!domainAvailable && checkedDomain !== values.domainName) {
      message.warning(HUB_SETUP.CHECK_DOMAIN_FIRST);
      return;
    }

    if (checkedDomain !== values.domainName) {
      message.warning(HUB_SETUP.DOMAIN_CHANGED);
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        dbUserAndHub,
        encryptUserAndHubPayload({
          operationType: 'write',
          action: 'setupHub',
          displayName: values.displayName,
          domainName: values.domainName,
          token,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const newToken = response.data?.token || response.data?.data?.token;
      const isSuccess =
        response.data?.statusCode === 200 || response.data?.success || !!newToken;

      if (isSuccess) {
        message.success(HUB_SETUP.SUBMIT_SUCCESS);
        onComplete?.(newToken);
        form.resetFields();
        setDomainAvailable(null);
        setCheckedDomain('');
      } else {
        message.error(response.data?.message || HUB_SETUP.SUBMIT_ERROR);
      }
    } catch (error) {
      console.error('Hub setup error:', error);
      message.error(error.response?.data?.message || HUB_SETUP.SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (e) => {
    if (onCancel) {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      onCancel();
      return;
    }

    form.resetFields();
    setDomainAvailable(null);
    setCheckedDomain('');
  };

  return (
    <Modal
      title={HUB_SETUP.TITLE}
      open={visible}
      closable
      maskClosable={false}
      onCancel={handleClose}
      footer={null}
      width={520}
      centered
      className={styles.hubModal}
      destroyOnClose
    >
      <div className={styles.infoBox}>
        <p className={styles.infoBoxText}>{HUB_SETUP.INFO_TEXT}</p>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item
          className={styles.displayNameField}
          label={HUB_SETUP.DISPLAY_NAME_LABEL}
          name="displayName"
          rules={[
            { required: true, message: HUB_SETUP.DISPLAY_NAME_REQUIRED },
            { min: 3, message: HUB_SETUP.DISPLAY_NAME_MIN },
            { max: 50, message: HUB_SETUP.DISPLAY_NAME_MAX },
          ]}
        >
          <Input data-guide-target={GUIDED_JOURNEY_TARGET.HUB_DISPLAY_NAME} placeholder={HUB_SETUP.DISPLAY_NAME_PLACEHOLDER} />
        </Form.Item>

        <div className={styles.domainSection}>
          <label className={styles.domainLabel}>
            <span className={styles.domainLabelStar}>*</span> {HUB_SETUP.DOMAIN_LABEL.replace('* ', '')}
          </label>
          <div className={styles.domainRow}>
            <Form.Item
              className={styles.domainInput}
              name="domainName"
              rules={[
                { required: true, message: HUB_SETUP.DOMAIN_REQUIRED },
                {
                  pattern: /^[a-z0-9-]+$/,
                  message: HUB_SETUP.DOMAIN_PATTERN,
                },
                { min: 3, message: HUB_SETUP.DOMAIN_MIN },
                { max: 30, message: HUB_SETUP.DOMAIN_MAX },
              ]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input
                data-guide-target={GUIDED_JOURNEY_TARGET.HUB_DOMAIN_NAME}
                placeholder={HUB_SETUP.DOMAIN_PLACEHOLDER}
                onChange={() => {
                  if (checkedDomain !== form.getFieldValue('domainName')) {
                    setDomainAvailable(null);
                  }
                }}
              />
            </Form.Item>
            <Button
              id={GUIDED_JOURNEY_TARGET.HUB_DOMAIN_CHECK}
              data-domain-checked={domainAvailable !== null ? "true" : "false"}
              data-domain-available={domainAvailable === true ? "true" : "false"}
              className={domainAvailable === true ? styles.checkBtnAvailable : styles.checkBtn}
              onClick={handleCheckDomain}
              loading={checkingDomain}
            >
              {HUB_SETUP.CHECK_BTN}
            </Button>
          </div>
          <div
            id={GUIDED_JOURNEY_TARGET.HUB_DOMAIN_PREVIEW}
            className={styles.domainPreview}
          >
            {HUB_SETUP.DOMAIN_URL_PREVIEW.replace("yourdomain", form.getFieldValue('domainName') || 'yourdomain')}
          </div>
        </div>

        {domainAvailable === true && (
          <div className={styles.domainSuccess}>
            <CheckOutlined />
            {HUB_SETUP.DOMAIN_AVAILABLE}
          </div>
        )}

        {domainAvailable === false && (
          <div className={styles.domainError}>
            <CloseOutlined />
            {HUB_SETUP.DOMAIN_TAKEN}
          </div>
        )}

        <Form.Item className={styles.submitWrapper}>
          <Button
            id={GUIDED_JOURNEY_TARGET.HUB_SUBMIT}
            className={styles.submitBtn}
            type="primary"
            htmlType="submit"
            block
            loading={submitting}
            disabled={domainAvailable !== true}
          >
            {submitting ? HUB_SETUP.SUBMITTING_BTN : HUB_SETUP.SUBMIT_BTN}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HubSetupBanner;
